import { useEffect, useState } from "react";
import clsx from "clsx";
import { CloseOutlined, Search } from "@material-ui/icons";
import { CircularProgress, makeStyles, TextField } from "@material-ui/core";
import { useDebounceFn } from "ahooks";
import { isPromise } from "@/helpers/isPromise";

const useStyles = makeStyles(theme => {
  const action = {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    display: "flex",
    alignItems: "center"
  };
  return {
    searchInput: {
      position: "relative",
      // width: "100%",
      "& .MuiFormControl-root": {
        width: "100% !important"
      }
    },
    left: {
      "& .MuiOutlinedInput-input": {
        paddingLeft: 32,
        paddingRight: 20
      },
      "& $action": {
        left: 10
      },
      "& .MuiInputLabel-outlined.MuiInputLabel-marginDense": {
        marginLeft: 16
      },
      "& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
        marginLeft: 0
      }
    },
    right: {
      "& $action": {
        right: 10
      }
    },
    clearable: {
      "& .MuiOutlinedInput-input": {
        paddingRight: 32
      }
    },
    hasIcon: {
      "$right&": {
        "& .MuiOutlinedInput-input": {
          paddingRight: 50
        }
      }
    },
    action,
    action2: {
      ...action,
      right: 10
    },
    close: {
      cursor: "pointer",
      fontSize: 16,
      color: theme.palette.text.secondary,

      "&:hover": {
        color: "#444"
      }
    }
  };
});

const SearchInput = ({
  size = "small",
  icon,
  label,
  iconPosition = "right",
  variant = "outlined",
  placeholder,
  name,
  disabled,
  clearable = true,
  loading: loadingFromProps,
  autoFocus,
  value,
  defaultValue,
  onChange,
  onPressEnter,
  onFocus,
  onBlur,
  className,
  style
}) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);

  const detectAsync = result => {
    if (isPromise(result)) {
      setLoading(true);
      result.finally(() => setLoading(false));
    }
  };

  const isIconRight = iconPosition === "right";
  const hasClear = clearable && !disabled;

  const actionClose = hasClear && !!value && (
    <CloseOutlined
      className={classes.close}
      onClick={() => onChange({ target: { name, value: "" } })}
    />
  );

  useEffect(() => {
    if (loadingFromProps !== undefined) {
      setLoading(loadingFromProps);
    }
  }, [loadingFromProps]);

  return (
    <div
      className={clsx(
        classes.searchInput,
        {
          [classes[iconPosition]]: iconPosition,
          [classes.clearable]: clearable,
          [classes.hasIcon]: icon === undefined || !!icon
        },
        className
      )}
      style={style}
    >
      <TextField
        autoFocus={autoFocus}
        className={classes.customInput}
        name={name}
        size={size}
        label={label}
        variant={variant}
        disabled={disabled}
        placeholder={placeholder}
        defaultValue={defaultValue}
        value={value}
        onChange={e => {
          detectAsync(onChange?.(e));
        }}
        onFocus={onFocus}
        inputProps={{ "aria-label": "search" }}
        onBlur={e => {
          detectAsync(onBlur?.(e));
        }}
        onKeyUp={e => {
          if (e.keyCode === 13) {
            detectAsync(onPressEnter?.(e));
          }
        }}
      />
      <div className={classes.action}>
        {isIconRight && actionClose}
        {loading && <CircularProgress size={17} color="primary" />}
        {!loading &&
          (icon === undefined ? <Search color="action" size={16} /> : icon)}
      </div>
      {!isIconRight && <div className={classes.action2}>{actionClose}</div>}
    </div>
  );
};

const DebouncedSearchInput = ({
  debounceOptions = {
    wait: 600,
    leading: false,
    trailing: true
  },
  onDebounceChange,
  ...props
}) => {
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState();

  const { run } = useDebounceFn(e => {
    const fn = onDebounceChange?.(e);
    if (!debounceOptions.leading && debounceOptions.trailing && isPromise(fn)) {
      setLoading(true);
      fn.finally(() => setLoading(false));
    }
    return fn;
  }, debounceOptions);

  const handleChange = e => {
    e.persist?.();
    setValue(e.target.value);
    props.onChange?.(e);
    return run(e);
  };

  return (
    <SearchInput
      {...props}
      loading={loading}
      value={value}
      onChange={handleChange}
    />
  );
};

export { DebouncedSearchInput };

export default SearchInput;
